import { NavigationPathsEnum } from "@utilities/enums";
import { useNavigation, useUser } from "@utilities/hooks";
import { useCallback, useEffect, useState } from "react";

type DirectionType = "left" | "right";

export const useController = () => {

    const { navigateTo } = useNavigation();
    const { 
        user, signinError,
        temporaryToken, createAccountError,
        onSigninUser, onCreateAccount,
        onResendMailOtp, onForgotPassword,
        forgotPasswordError, setTemporaryToken,
        setCreatePasswordError,
    } = useUser();

    const [componentIndex, setComponentIndex] = useState<number>(0);
    const [animateInDirection, setAnimateInDirection] = useState<string>("");
    const [animateOutDirection, setAnimateOutDirection] = useState<string>("");
    const [isForgotPasswordEmailSent, setIsForgotPasswordEmailSent] = useState<boolean>(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>("");
    const [isLoadingSignin, setIsLoadingSignin] = useState<boolean>(false);
    const [isLoadingEmailVerification, setIsLoadingEmailVerification] = useState<boolean>(false);
    const [isLoadingForgotPassword, setIsLoadingForgotPassword] = useState<boolean>(false);

    useEffect(() => {
        if (animateOutDirection) {
          const timeout = setTimeout(() => {
            setAnimateOutDirection("");
          }, 500);
          return () => clearTimeout(timeout);
        }
    }, [animateOutDirection]);
    
    useEffect(() => {
        if (animateInDirection) {
            const timeout = setTimeout(() => {
            setAnimateInDirection("");
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [animateInDirection]);

    useEffect(()=> {
        if(user){
            navigateTo(NavigationPathsEnum.SHOP);
        }
    }, [user]);

    const onSignIn = useCallback(async (email: string, password: string) => {
        if(email && password){
            setIsLoadingSignin(true);
            await onSigninUser(email, password);
            setIsLoadingSignin(false);
        }
    }, [onSigninUser]);

    const onSentEmail = useCallback(async(email: string) => {
        if(email){
            setIsLoadingEmailVerification(true);
            await onCreateAccount(email);
            setIsLoadingEmailVerification(false);
        }
    }, [onCreateAccount]);

    const onResendEmail = useCallback(async() => {
        if(temporaryToken){
            setIsLoadingEmailVerification(true);
            await onResendMailOtp(temporaryToken);
            setIsLoadingEmailVerification(false);
        }
    }, [temporaryToken, onResendMailOtp]);

    const onForgotPasswordEmail = useCallback(async (email?: string) => {
        const emailToUse = email || forgotPasswordEmail;
        
        if (emailToUse) {
            setIsLoadingForgotPassword(true);
            
            const result = await onForgotPassword(emailToUse);
            
            if (result) {
                setForgotPasswordEmail(emailToUse);
                setIsForgotPasswordEmailSent(true);
            }
            
            setIsLoadingForgotPassword(false);
        }
    }, [forgotPasswordEmail, onForgotPassword]);

    const handleSwitchComponent = useCallback((index: number, direction: DirectionType) => {
        setAnimateOutDirection(direction);

        setTimeout(() => {
            setComponentIndex(index);
            setAnimateInDirection(direction === "left" ? "right" : "left");
        }, 500);
    }, []);

    const onClearStore = useCallback(() => {
        setTemporaryToken("");
        setCreatePasswordError(null);
        setIsForgotPasswordEmailSent(false);
        setForgotPasswordEmail("");
    }, [setTemporaryToken, setCreatePasswordError]);

    return {
        animateOutDirection, temporaryToken, createAccountError,
        isLoadingSignin, signinError, componentIndex,
        animateInDirection, isForgotPasswordEmailSent,
        isLoadingEmailVerification, isLoadingForgotPassword,
        forgotPasswordEmail, forgotPasswordError,
        onSignIn, onSentEmail, onResendEmail,
        handleSwitchComponent, onForgotPasswordEmail,
        onClearStore,
    };
};
